'use client';

import React from 'react';
import { Globe, Users, Binoculars } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AUDIENCES,
  AUDIENCE_DISCLOSURE,
  AUDIENCE_HINT,
  AUDIENCE_LABEL,
  type Audience,
} from '@/lib/audience';

const ICONS: Record<Audience, typeof Globe> = {
  Public: Globe,
  Friends: Users,
  Watchers: Binoculars, // the site's Watching icon (ProfileMenu, FollowButton)
};

type Props = {
  value: Audience;
  onChange: (next: Audience) => void;
  disabled?: boolean;
  /** Legend text. Defaults to "Who can see this". */
  legend?: string;
  /** Extra classes on the wrapping fieldset. */
  className?: string;
  /** Element id prefix so two pickers on one page never collide. */
  idPrefix?: string;
};

/**
 * The one audience control, shared by gallery and video create/edit forms so
 * all four surfaces look and read the same. Same radiogroup-of-cards pattern as
 * PostCategoryPicker. Always renders the staff-review disclosure underneath:
 * that line is part of the control, not optional copy.
 */
export default function AudiencePicker({
  value,
  onChange,
  disabled = false,
  legend = 'Who can see this',
  className,
  idPrefix = 'audience',
}: Props) {
  return (
    <fieldset className={cn('space-y-2', className)} disabled={disabled}>
      <legend className="text-sm font-medium text-foreground">{legend}</legend>
      <div
        role="radiogroup"
        aria-label={legend}
        className="grid grid-cols-1 gap-2 sm:grid-cols-3"
      >
        {AUDIENCES.map((a) => {
          const selected = value === a;
          const Icon = ICONS[a];
          return (
            <button
              key={a}
              id={`${idPrefix}-${a.toLowerCase()}`}
              type="button"
              role="radio"
              aria-checked={selected}
              data-audience={a}
              onClick={() => onChange(a)}
              disabled={disabled}
              className={cn(
                'flex flex-col items-start gap-1.5 rounded-xl border p-4 text-left transition-colors outline-none',
                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                selected
                  ? 'border-foreground bg-accent/40'
                  : 'border-border hover:border-foreground/40 hover:bg-accent/20',
                disabled && 'pointer-events-none opacity-50',
              )}
            >
              <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
              <span className="text-sm font-semibold">{AUDIENCE_LABEL[a]}</span>
              <span className="text-xs leading-snug text-muted-foreground">{AUDIENCE_HINT[a]}</span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="audience-disclosure">
        {AUDIENCE_DISCLOSURE}
      </p>
    </fieldset>
  );
}
